/**
 * Hotbar Service
 * Manages asset hotbars for quick access in tool panels
 * Supports 3 presets per asset type, stored in localStorage
 */

import { AssetType } from '../types/asset';

/** Number of slots in each hotbar (2 rows x 4 columns) */
export const HOTBAR_SIZE = 8;

/** Number of hotbar presets available */
export const HOTBAR_PRESET_COUNT = 3;

/** localStorage key prefix for hotbars */
const HOTBAR_STORAGE_KEY = 'asset-hotbar';

/** localStorage key for active preset index */
const ACTIVE_PRESET_KEY = 'asset-hotbar-active-preset';

/**
 * Hotbar slot - can be empty (null) or contain an asset name
 */
export type HotbarSlot = string | null;

/**
 * Hotbar configuration for a specific asset type
 */
export interface Hotbar {
  slots: HotbarSlot[];
}

/**
 * All hotbar presets for an asset type
 */
export interface HotbarPresets {
  presets: Hotbar[];
  activePreset: number;
}

/**
 * Gets the localStorage key for a specific asset type hotbar presets
 */
function getStorageKey(assetType: AssetType): string {
  return `${HOTBAR_STORAGE_KEY}-${assetType}`;
}

/**
 * Gets the localStorage key for active preset
 */
function getActivePresetKey(assetType: AssetType): string {
  return `${ACTIVE_PRESET_KEY}-${assetType}`;
}

/**
 * Creates an empty hotbar with all null slots
 */
export function createEmptyHotbar(): Hotbar {
  return {
    slots: Array(HOTBAR_SIZE).fill(null)
  };
}

/**
 * Creates empty hotbar presets
 */
export function createEmptyPresets(): HotbarPresets {
  return {
    presets: Array(HOTBAR_PRESET_COUNT).fill(null).map(() => createEmptyHotbar()),
    activePreset: 0
  };
}

/**
 * Loads all hotbar presets from localStorage
 * @param assetType - The asset type
 * @param validAssetNames - Optional set of valid asset names to filter against during load
 */
export function loadHotbarPresets(assetType: AssetType, validAssetNames?: Set<string>): HotbarPresets {
  try {
    const stored = localStorage.getItem(getStorageKey(assetType));
    const activePresetStr = localStorage.getItem(getActivePresetKey(assetType));
    const activePreset = activePresetStr ? parseInt(activePresetStr, 10) : 0;

    if (stored) {
      const parsed = JSON.parse(stored);
      let hasChanges = false;

      /**
       * Filters a slot value against valid asset names if provided
       */
      const filterSlot = (slot: string | null): string | null => {
        if (slot === null) return null;
        if (validAssetNames && !validAssetNames.has(slot)) {
          hasChanges = true;
          return null;
        }
        return slot;
      };

      // Handle old format (single hotbar) - migrate to new format
      if (Array.isArray(parsed.slots)) {
        const presets = createEmptyPresets();
        presets.presets[0] = { slots: parsed.slots.slice(0, HOTBAR_SIZE).map(filterSlot) };
        while (presets.presets[0].slots.length < HOTBAR_SIZE) {
          presets.presets[0].slots.push(null);
        }
        presets.activePreset = 0;
        // Save migrated format (always save for migration, or if invalid slots were removed)
        saveHotbarPresets(assetType, presets);
        return presets;
      }

      // New format with presets array
      if (Array.isArray(parsed.presets)) {
        const presets: Hotbar[] = [];
        for (let i = 0; i < HOTBAR_PRESET_COUNT; i++) {
          if (parsed.presets[i] && Array.isArray(parsed.presets[i].slots)) {
            const slots = parsed.presets[i].slots.slice(0, HOTBAR_SIZE).map(filterSlot);
            while (slots.length < HOTBAR_SIZE) {
              slots.push(null);
            }
            presets.push({ slots });
          } else {
            presets.push(createEmptyHotbar());
          }
        }
        const result = {
          presets,
          activePreset: Math.min(Math.max(0, activePreset), HOTBAR_PRESET_COUNT - 1)
        };
        // Save if invalid slots were removed
        if (hasChanges) {
          saveHotbarPresets(assetType, result);
        }
        return result;
      }
    }
  } catch (err) {
    console.error(`Failed to load hotbar presets for ${assetType}:`, err);
  }
  return createEmptyPresets();
}

/**
 * Validates hotbar presets against a list of valid asset names
 * Removes any slots that reference non-existent assets
 * @param assetType - The asset type
 * @param validAssetNames - Set of valid asset names for this type
 * @returns The cleaned presets (also saved to localStorage if changes were made)
 */
export function validateHotbarPresets(assetType: AssetType, validAssetNames: Set<string>): HotbarPresets {
  // loadHotbarPresets now handles validation internally when validAssetNames is provided
  return loadHotbarPresets(assetType, validAssetNames);
}

/**
 * Loads the active hotbar from localStorage (convenience function)
 */
export function loadHotbar(assetType: AssetType): Hotbar {
  const presets = loadHotbarPresets(assetType);
  return presets.presets[presets.activePreset];
}

/**
 * Gets the active preset index
 */
export function getActivePresetIndex(assetType: AssetType): number {
  const presets = loadHotbarPresets(assetType);
  return presets.activePreset;
}

/**
 * Saves all hotbar presets to localStorage
 */
export function saveHotbarPresets(assetType: AssetType, presets: HotbarPresets): void {
  try {
    localStorage.setItem(getStorageKey(assetType), JSON.stringify({ presets: presets.presets }));
    localStorage.setItem(getActivePresetKey(assetType), presets.activePreset.toString());
  } catch (err) {
    console.error(`Failed to save hotbar presets for ${assetType}:`, err);
  }
}

/**
 * Sets the active preset index
 */
export function setActivePreset(assetType: AssetType, presetIndex: number): HotbarPresets {
  const presets = loadHotbarPresets(assetType);
  presets.activePreset = Math.min(Math.max(0, presetIndex), HOTBAR_PRESET_COUNT - 1);
  saveHotbarPresets(assetType, presets);
  return presets;
}

/**
 * Adds an asset to a specific slot in a specific preset
 */
export function setHotbarSlot(assetType: AssetType, presetIndex: number, slotIndex: number, assetName: string | null): HotbarPresets {
  const presets = loadHotbarPresets(assetType);
  const preset = presets.presets[presetIndex];

  if (slotIndex >= 0 && slotIndex < HOTBAR_SIZE) {
    // If adding an asset, remove it from any other slot in this preset first
    if (assetName !== null) {
      preset.slots = preset.slots.map(slot => slot === assetName ? null : slot);
    }
    preset.slots[slotIndex] = assetName;
    saveHotbarPresets(assetType, presets);
  }
  return presets;
}

/**
 * Removes an asset from a preset (all slots)
 */
export function removeFromHotbar(assetType: AssetType, presetIndex: number, assetName: string): HotbarPresets {
  const presets = loadHotbarPresets(assetType);
  const preset = presets.presets[presetIndex];
  preset.slots = preset.slots.map(slot => slot === assetName ? null : slot);
  saveHotbarPresets(assetType, presets);
  return presets;
}

/**
 * Checks if an asset is in a specific preset
 */
export function isInHotbar(assetType: AssetType, presetIndex: number, assetName: string): boolean {
  const presets = loadHotbarPresets(assetType);
  return presets.presets[presetIndex].slots.includes(assetName);
}

/**
 * Auto-fills empty slots in a preset with assets
 */
export function autoFillHotbar(assetType: AssetType, presetIndex: number, assetNames: string[]): HotbarPresets {
  const presets = loadHotbarPresets(assetType);
  const preset = presets.presets[presetIndex];
  let assetIndex = 0;

  for (let i = 0; i < HOTBAR_SIZE && assetIndex < assetNames.length; i++) {
    if (preset.slots[i] === null) {
      while (assetIndex < assetNames.length && preset.slots.includes(assetNames[assetIndex])) {
        assetIndex++;
      }
      if (assetIndex < assetNames.length) {
        preset.slots[i] = assetNames[assetIndex];
        assetIndex++;
      }
    }
  }

  saveHotbarPresets(assetType, presets);
  return presets;
}
